import { useCursor } from '@react-three/drei';
import { ThreeElements, Color } from '@react-three/fiber';
import { useState } from 'react';
import { a, useSpring, config } from '@react-spring/three';
import { useMutableCallback } from '@slope/hooks';

export type ChipProps = ThreeElements['mesh'] & {
  /**
   * The color of the chip.
   * Can be a string, number, THREE.Color, or an array of RGB values.
   */
  color?: Color;
  /**
   * The radius of the chip.
   * @default 3
   */
  radius?: number;
  /**
   * The height of the chip.
   * @default 1
   */
  height?: number;
  /**
   * The number of segments for the cylinder geometry.
   * @default 32
   */
  segments?: number;
};

/**
 * A simple chip component that renders a cylinder mesh.
 * It can be used to represent a token or a marker in a 3D scene.
 */
export const Chip: React.FC<ChipProps> = ({
  color,
  radius = 3,
  height = 1,
  segments = 32,
  children,
  ...restProps
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const springProps = useSpring(() => ({
    scale: isHovered ? 2 : 1,
    position: isHovered ? [0, height / 2, 0] : [0, 0, 0],
    config: config.stiff,
  }));

  useCursor(isHovered, 'grab');

  const handlePointerOver = useMutableCallback(() => {
    setIsHovered(true);
  });
  const handlePointerOut = useMutableCallback(() => {
    setIsHovered(false);
  });

  return (
    <a.mesh
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      {...springProps}
      {...restProps}
    >
      <cylinderGeometry args={[radius, radius, height, segments]} />
      <meshStandardMaterial color={color} />
      {children}
    </a.mesh>
  );
};
